import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  MenuToggle,
  type MenuToggleElement,
  Select,
  SelectList,
  SelectOption,
  TextInputGroup,
  TextInputGroupMain,
  TextInputGroupUtilities,
} from '@patternfly/react-core';
import TimesIcon from '@patternfly/react-icons/dist/esm/icons/times-icon';

export interface ClientOption {
  id: number;
  name: string;
}

// Real client ids are positive, so -1 is a safe sentinel for the disabled "no match" row — keeping
// every SelectOption value numeric (no string/number mixing in onSelect).
const NO_RESULTS_ID = -1;

interface ClientTypeaheadProps {
  clients: ClientOption[];
  /** Selected client id, or null when nothing is chosen. */
  value: number | null;
  onChange: (id: number | null) => void;
  isDisabled?: boolean;
  /**
   * DOM id prefix — MUST be unique per simultaneously-mounted instance (a modal's picker over a
   * toolbar filter would otherwise duplicate ids and mislabel both). The default preserves the
   * original ids for the pre-existing single-instance call sites.
   */
  idPrefix?: string;
}

// Searchable single-select client picker. Mirrors the CountrySelectField typeahead in
// CreateCertificateModal.tsx (swap country code/name → client id/name): you own the filtering,
// open state, and arrow-key navigation; the clear (×) button is rendered manually.
function ClientTypeahead({
  clients,
  value,
  onChange,
  isDisabled = false,
  idPrefix = 'admin-override-client',
}: ClientTypeaheadProps) {
  const inputId = `${idPrefix}-input`;
  const listboxId = `${idPrefix}-listbox`;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(() => {
    const client = clients.find((c) => c.id === value);
    return client ? client.name : '';
  });
  const [filterValue, setFilterValue] = useState<string>('');
  const [focusedItemIndex, setFocusedItemIndex] = useState<number | null>(null);
  const textInputRef = useRef<HTMLInputElement | null>(null);

  // When the menu closes, snap the visible text back to the committed selection — typing without
  // choosing is discarded and reverts to whichever client is actually stored in `value`.
  useEffect(() => {
    if (!isOpen) {
      const client = clients.find((c) => c.id === value);
      setInputValue(client ? client.name : '');
      setFilterValue('');
      setFocusedItemIndex(null);
    }
  }, [value, isOpen, clients]);

  const filtered = useMemo<ClientOption[]>(() => {
    const q = filterValue.trim().toLowerCase();
    if (!q) {
      return clients;
    }
    return clients.filter((c) => c.name.toLowerCase().includes(q) || String(c.id).includes(q));
  }, [clients, filterValue]);

  const optionItems: ClientOption[] =
    filtered.length === 0 ? [{ id: NO_RESULTS_ID, name: 'No clients match' }] : filtered;

  const closeMenu = () => {
    setIsOpen(false);
    setFocusedItemIndex(null);
  };

  const onSelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    chosen: string | number | undefined,
  ) => {
    if (typeof chosen !== 'number' || chosen === NO_RESULTS_ID) {
      return;
    }
    const client = clients.find((c) => c.id === chosen);
    if (!client) {
      return;
    }
    onChange(client.id);
    setInputValue(client.name);
    setFilterValue('');
    closeMenu();
  };

  const onTextInputChange = (_event: React.FormEvent<HTMLInputElement>, next: string) => {
    setInputValue(next);
    setFilterValue(next);
    if (!isOpen) {
      setIsOpen(true);
    }
    setFocusedItemIndex(null);
  };

  const handleArrowKeys = (key: 'ArrowUp' | 'ArrowDown') => {
    if (!isOpen) {
      setIsOpen(true);
    }
    if (optionItems.length === 0 || optionItems[0].id === NO_RESULTS_ID) {
      return;
    }
    const last = optionItems.length - 1;
    if (key === 'ArrowDown') {
      setFocusedItemIndex(focusedItemIndex === null || focusedItemIndex === last ? 0 : focusedItemIndex + 1);
    } else {
      setFocusedItemIndex(focusedItemIndex === null || focusedItemIndex === 0 ? last : focusedItemIndex - 1);
    }
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Enter': {
        event.preventDefault();
        if (focusedItemIndex !== null) {
          const item = optionItems[focusedItemIndex];
          if (item.id !== NO_RESULTS_ID) {
            onSelect(undefined, item.id);
          }
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      }
      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault();
        handleArrowKeys(event.key);
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          closeMenu();
        }
        break;
    }
  };

  const onToggleClick = () => {
    setIsOpen((open) => !open);
    textInputRef.current?.focus();
  };

  const onClearButtonClick = () => {
    onChange(null);
    setInputValue('');
    setFilterValue('');
    setFocusedItemIndex(null);
    textInputRef.current?.focus();
  };

  const toggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      variant="typeahead"
      aria-label="Client"
      onClick={onToggleClick}
      isExpanded={isOpen}
      isFullWidth
      isDisabled={isDisabled}
    >
      <TextInputGroup isPlain>
        <TextInputGroupMain
          value={inputValue}
          onClick={() => {
            if (!isOpen) {
              setIsOpen(true);
            }
          }}
          onChange={onTextInputChange}
          onKeyDown={onInputKeyDown}
          id={inputId}
          autoComplete="off"
          innerRef={textInputRef}
          placeholder="Type a client name"
          aria-label="Client"
          role="combobox"
          isExpanded={isOpen}
          aria-controls={listboxId}
        />
        <TextInputGroupUtilities {...(!inputValue ? { style: { display: 'none' } } : {})}>
          <Button variant="plain" onClick={onClearButtonClick} aria-label="Clear client" icon={<TimesIcon />} />
        </TextInputGroupUtilities>
      </TextInputGroup>
    </MenuToggle>
  );

  return (
    <Select
      id={idPrefix}
      isOpen={isOpen}
      selected={value ?? undefined}
      onSelect={onSelect}
      onOpenChange={(open) => {
        if (!open) {
          closeMenu();
        }
      }}
      toggle={toggle}
      variant="typeahead"
    >
      <SelectList id={listboxId}>
        {optionItems.map((option, index) => (
          <SelectOption
            key={option.id}
            value={option.id}
            isFocused={focusedItemIndex === index}
            isAriaDisabled={option.id === NO_RESULTS_ID}
          >
            {option.name}
          </SelectOption>
        ))}
      </SelectList>
    </Select>
  );
}

export default ClientTypeahead;
